// English — the reference dictionary. Every other language must define every key (checked at compile time).
// {name} placeholders are filled by t(); { one, other } entries are plural-aware.
export const en = {
  // App chrome
  'app.options': 'Options',
  'app.connected': 'Connected',
  'app.offline': 'Offline',
  'app.views': 'Views',
  'tab.map': 'Map',
  'tab.list': 'Villages',
  'tab.sources': 'Sources',
  'common.done': 'Done',
  'common.close': 'Close',
  'common.loading': 'Loading…',
  'common.back': 'Back to list',

  // Bands
  'band.H': 'High',
  'band.M': 'Medium',
  'band.L': 'Low',
  'band.H.short': 'High',
  'band.M.short': 'Med',
  'band.L.short': 'Low',
  'band.aria': '{band} confidence, score {score} of 100',

  // Primary screen
  'primary.question': 'Which villages need evacuation support first?',
  'primary.subtitle': {
    one: '{n} Kodagu village ranked by confidence · as of {time} IST',
    other: '{n} Kodagu villages ranked by confidence · as of {time} IST',
  },
  'primary.replay': ' · 2018 flood replay',
  'primary.hint': 'Top card = help first.',
  'primary.hintLink': 'How scores work',
  'primary.hintDismiss': 'Dismiss hint',
  'primary.filterGroup': 'Filter by confidence',
  'primary.filterPlaceholder': 'Filter by village or taluka',
  'primary.loading': 'Loading ranked villages…',
  'primary.noMatch': 'No villages match.',
  'primary.listLabel': 'Villages ranked by evacuation need',

  // Card
  'card.rankTaluka': '#{rank} · {taluka} taluka',
  'card.taluka': '{taluka} taluka',
  'card.signals': { one: '{n} signal', other: '{n} signals' },
  'card.silent': '{source} silent',
  'card.aria': '{place}: {reason}. Tap for details.',
  'card.oldest': 'oldest {age}',
  'card.stale': 'stale',
  'card.conflict': 'Sources disagree',

  // Banners
  'banner.offline': 'Offline · showing last data from {time}',
  'banner.noCache': 'Offline · no data cached on this phone yet',
  'banner.expired': 'Offline · data older than 24 hours',
  'banner.stale': 'Sources refreshing…',

  // Latest changes
  'upd.title': 'Latest changes',
  'upd.less': 'Less',
  'upd.all': 'All {n}',
  'upd.new': '{place} appeared at #{rank} · {band} {score}',
  'upd.up': '{place} rose to {band} ({from} → {to})',
  'upd.down': '{place} fell to {band} ({from} → {to})',
  'upd.conflict': '{place}: sources now disagree',
  'upd.resolved': '{place}: disagreement cleared',
  'upd.report': {
    one: '{place}: {n} new signal · now {score}',
    other: '{place}: {n} new signals · now {score}',
  },

  // Detail
  'detail.dialog': 'Recommendation details',
  'detail.openMaps': 'Open in maps',
  'detail.openMapsAria': 'Open {place} in maps, {lat} north, {lon} east',
  'detail.why': 'Why {score}?',
  'detail.sameFormula': 'same formula everywhere',
  'detail.part.fresh': 'Fresh',
  'detail.part.fresh.gloss': 'How recent is the newest signal',
  'detail.part.agree': 'Agreement',
  'detail.part.agree.gloss': 'Independent sources saying the same',
  'detail.part.trust': 'Trust',
  'detail.part.trust.gloss': 'Sensors › satellite › partners › SMS',
  'detail.saysSafe': 'Says safe ({n})',
  'detail.saysHazard': 'Says hazard ({n})',
  'detail.signals': 'Signals ({n})',
  'detail.ago': '{age} ago',
  'detail.offlineMissing': 'Can’t load details offline for this village yet.',
  'detail.send': 'Send to team',
  'detail.sms': 'SMS',
  'detail.smsAria': 'Send by SMS',
  'detail.copy': 'Copy',
  'detail.copied': 'Copied',
  'detail.copyAria': 'Copy dispatch message',

  // Trust tiers
  'tier.T1': 'Official sensor',
  'tier.T2': 'Satellite',
  'tier.T3': 'Partner data',
  'tier.T4': 'Citizen',
  'tierGroup.T1': 'Official sensors',
  'tierGroup.T2': 'Satellite',
  'tierGroup.T3': 'Partner data',
  'tierGroup.T4': 'Citizens',

  // Ages
  'age.min': { one: '{n} minute', other: '{n} minutes' },
  'age.hour': { one: '{n} hour', other: '{n} hours' },
  'age.day': { one: '{n} day', other: '{n} days' },
  'age.short.min': '{n} min',
  'age.short.hour': '{n} h',
  'age.short.day': '{n} d',
  'age.short.sec': '{n} s',

  // Reasons (built from structured data; English reasons come from the server verbatim)
  'reason.none': 'No signals',
  'reason.stale': 'Stale: ',
  'reason.agree': '{list} agree, all within {age}',
  'reason.single': 'Single {what}, {age} old, no other source agrees',
  'reason.singleUnverified': 'Single unverified {what}, {age} old, no other source agrees',
  'reason.conflict': 'Conflict: {support} say flooded, {oppose} say safe, within {age}',
  'reason.count': { one: '{what}', other: '{n} {what}' },
  'reason.more': { one: '{n} more signal', other: '{n} more signals' },

  // Event types
  'ev.flood_extent': { one: 'satellite flood extent', other: 'satellite flood detections' },
  'ev.heavy_rain': { one: 'heavy-rain reading', other: 'heavy-rain readings' },
  'ev.river_danger': {
    one: 'river gauge above danger mark',
    other: 'river gauges above danger mark',
  },
  'ev.road_impassable': { one: 'blocked-road report', other: 'blocked-road reports' },
  'ev.power_outage': { one: 'power-outage ping', other: 'power-outage pings' },
  'ev.thermal_anomaly': { one: 'thermal hotspot', other: 'thermal hotspots' },
  'ev.earthquake': { one: 'earthquake alert', other: 'earthquake alerts' },
  'ev.multi_hazard_alert': { one: 'GDACS alert', other: 'GDACS alerts' },
  'ev.citizen_report': { one: 'SMS report', other: 'SMS reports' },

  // Signal summaries (detail view)
  'sum.river': '{station}: {level} m (danger mark {danger} m)',
  'sum.floodProb': 'Flood probability {p}% in 500 m cell',
  'sum.flood': 'Flood extent detected in 500 m cell',
  'sum.rainRate': 'Rain {mm} mm/h',
  'sum.rainDay': 'District rainfall {mm} mm / 24 h',
  'sum.rainWarning': 'Heavy-rain warning',
  'sum.road': '{road} blocked',
  'sum.roadLandslide': '{road} blocked (landslide)',
  'sum.roadDefault': 'Road',
  'sum.power': 'Outage on {feeder}',
  'sum.synthetic': '(synthetic)',

  // Sources
  'src.title': 'Data sources',
  'src.subtitle':
    '{n} of {total} sources reported in the last 24 h. Delay is the typical time from observation to arrival here.',
  'src.mode.live': 'Live',
  'src.mode.replay': 'Replay 2018',
  'src.mode.mock': 'Mock adapter',
  'src.notInArchive': 'not in 2018 archive',
  'src.noData': 'no data yet',
  'src.last': 'last {age} ago',
  'src.count24h': '{n} in 24 h',
  'src.delay': 'delay',
  'src.error': 'Error: {msg}',
  'src.healthy': 'healthy',
  'src.notReporting': 'not reporting',
  'src.notInReplay': 'not in replay',
  'src.failTitle': 'What breaks, and what happens',
  'src.fail1.t': 'Phone network goes down',
  'src.fail1.b':
    'The last ranking and every opened village stay on the phone. A blue banner shows how old they are. When signal returns, the app catches up by itself.',
  'src.fail2.t': 'A satellite or sensor feed goes silent',
  'src.fail2.b':
    'Scores lose freshness automatically, the card says “stale”, and a chip names the silent source, so a quiet feed never looks like good news.',
  'src.fail3.t': 'Reports contradict each other',
  'src.fail3.b':
    'Opposing reports cancel out agreement instead of averaging it away. The card is flagged “Sources disagree” and lists both sides for a person to judge.',
  'src.fail4.t': 'Spam or spoofed SMS',
  'src.fail4.b':
    'Citizen SMS carry the lowest trust tier, each number is rate-limited, and one sender can never outweigh a sensor. Tiers are fixed in code, so a feed can’t promote itself.',
  'src.weights': 'Confidence weights are public:',

  // Source names
  'source.cwc-wris': 'CWC river gauges (India-WRIS)',
  'source.nwdp': 'National Water Data Portal',
  'source.usgs-quake': 'USGS earthquake feed',
  'source.sentinel-1-cdse': 'Sentinel-1 SAR flood extent',
  'source.sentinel-2-cdse': 'Sentinel-2 optical',
  'source.cems-gfm': 'Copernicus EMS Global Flood Monitoring',
  'source.firms-nasa': 'NASA FIRMS thermal anomalies',
  'source.openweather': 'OpenWeather One Call 3.0',
  'source.imd': 'IMD nowcasts & warnings',
  'source.overpass': 'OSM road status (Overpass)',
  'source.gdacs': 'GDACS multi-hazard alerts',
  'source.twilio-sms': 'Citizen SMS (Twilio)',
  'source.msg91-sms': 'Citizen SMS (MSG91, India)',
  'source.discom-outage': 'DISCOM power outages (mock adapter)',
  'sourceShort.sentinel-1-cdse': 'satellite radar',
  'sourceShort.sentinel-2-cdse': 'satellite optical',
  'sourceShort.cems-gfm': 'Copernicus GFM',
  'sourceShort.firms-nasa': 'NASA FIRMS',
  'sourceShort.cwc-wris': 'CWC gauge',
  'sourceShort.nwdp': 'NWDP gauge',
  'sourceShort.usgs-quake': 'USGS',

  // Map
  'map.aria': 'Map of ranked villages in Kodagu',
  'map.loading': 'Loading map…',
  'map.downloading': 'Downloading offline map… {p}%',
  'map.details': 'Details',

  // Options
  'opt.display': 'Display',
  'opt.theme': 'Theme',
  'opt.day': 'Day',
  'opt.night': 'Night',
  'opt.sun': 'Sunlight',
  'opt.textSize': 'Text size',
  'opt.standard': 'Standard text',
  'opt.large': 'Large text',
  'opt.language': 'Language',
  'opt.languageNote': 'Village names on the map stay in English.',
  'opt.understand': 'Understand the score',
  'opt.presenter': 'Presenter · 2018 replay',
  'opt.presenterNote': 'Controls affect this server for everyone viewing it.',
  'opt.presenterLocked': 'Locked on this server (DEMO_CONTROLS=off).',
  'opt.replaySpeed': 'Replay speed',
  'opt.pause': 'Pause',
  'opt.restart': 'Restart at 09:00 IST',
  'opt.paused': 'Paused.',
  'opt.running': 'Running at {speed}×.',
  'opt.restarted': 'Back to 09:00 IST, 16 Aug 2018.',
  'opt.unreachable': 'Could not reach the server.',
  'opt.speedIs': 'Replay speed {speed}×.',
  'opt.drill': 'Comprehension drill',
  'opt.drillNote':
    'Times a volunteer from hand-over until they name the top village and a reason (target: median under 90 s). Results stay on this phone.',
  'opt.drillStart': 'Start drill mode',
  'opt.drillRuns': { one: '{n} run · median {median} s', other: '{n} runs · median {median} s' },
  'opt.phone': 'This phone',
  'opt.install': 'Install ClearSignal',
  'opt.installIos': 'To install: Share → Add to Home Screen.',
  'opt.installOther': 'Installed, or install from the browser menu (⋮ → Install app).',
  'opt.offlineNote': 'Works offline after the first load. Kodagu district · v{version}',

  // How scores work
  'how.title': 'How scores work',
  'how.intro':
    'Every village gets a score from 0 to 100: how sure we are that it needs help now. It is a simple, published formula. No AI decides the ranking.',
  'how.parts': 'The score is three parts added up',
  'how.upTo': '{part} · up to {points} points',
  'how.fresh':
    'How recent is the newest signal? Rain readings go stale in minutes; satellite passes last hours.',
  'how.agree':
    'How many independent sources say the same thing? Four different kinds of source is full marks.',
  'how.trust': 'How reliable are those sources? Official sensors count most, a single SMS least.',
  'how.colours': 'What the colours mean',
  'how.H': 'Several independent sources agree, recently. Act on it.',
  'how.M': 'Some evidence. Verify, or act if resources allow.',
  'how.L': 'Weak, old or single-source. Watch, don’t dispatch yet.',
  'how.range': '{band} · {from}–{to}',
  'how.warnings': 'Warnings on a card',
  'how.conflict':
    'Sources disagree: someone reports the village safe while others report flooding. Both sides are listed; a person decides.',
  'how.stale': 'Stale: nothing fresh has arrived. The score drops on its own as signals age.',
  'how.tiers': 'Trust tiers (fixed; a feed can’t promote itself)',
  'how.tier.T1': 'River gauges (CWC, NWDP), USGS',
  'how.tier.T2': 'Sentinel-1/2, Copernicus flood maps, NASA FIRMS',
  'how.tier.T3': 'OpenWeather, IMD, road status, GDACS',
  'how.tier.T4': 'SMS from any phone, power-outage reports',
  'how.data': 'Your data',
  'how.dataBody':
    'Citizen phone numbers are never stored. Each is replaced by a one-way code, and numbers typed inside messages are removed. See the full data policy in the project documents.',
  'how.weights': 'Exact numbers, public and auditable:',

  // Drill
  'drill.mode': 'Drill mode · participant {n}',
  'drill.handOver': 'Hand the phone over and ask:',
  'drill.question': '“Which village should we send help to first, and why should you trust that?”',
  'drill.start': 'Start timer',
  'drill.exit': 'Exit drill mode',
  'drill.participant': 'Participant {n}',
  'drill.topShown': 'Top village on screen: {place}. Did they name it?',
  'drill.reason': 'Reason they gave (optional)',
  'drill.reasonPlaceholder': 'e.g. river gauges and satellite agree',
  'drill.correct': 'Correct',
  'drill.wrong': 'Wrong',
  'drill.resume': 'Resume timer',
  'drill.timerAria': 'Drill timer {time}. Tap to stop.',
  'drill.stop': 'Stop',
  'drill.time': 'Time',
  'drill.yes': 'Yes',
  'drill.no': 'No',
  'drill.copyCsv': 'Copy CSV',
  'drill.copied': 'Copied',
  'drill.download': 'Download',
  'drill.clear': 'Clear',
  'drill.clearConfirm': 'Delete all drill results on this phone?',
  'drill.dialog': 'Drill mode',
  'drill.recordDialog': 'Record drill result',

  // Dispatch message (sent to rescue teams)
  'dispatch.head': 'ClearSignal {date} {time} IST',
  'dispatch.priority': 'PRIORITY #{rank}: ',
  'dispatch.place': '{place} ({taluka} taluka)',
  'dispatch.confidence': 'Confidence {band} {score}/100 · oldest signal {age}',
  'dispatch.why': 'Why: {reason}',
  'dispatch.caution': 'CAUTION: sources disagree, verify on arrival',
  'dispatch.location': 'Location: {lat}N {lon}E {url}',
} as const;

export type MessageKey = keyof typeof en;
